// Encrypted backup file `.feelbackup` (Implementation Plan v02 §6.4, IR-26, IR-34;
// key-custody.md "Backup file key"; I-1a Batch 6a).
//
// Layout (one file, read whole):
//   magic    8 bytes   "PEBBLEBK"
//   length   2 bytes   big-endian byte length of the header
//   header   UTF-8 JSON, neutral: format version, app / data-schema / package
//            versions, Argon2id cost + salt, AES-GCM nonce. No dates, no
//            counts, no content.
//   sealed   AES-GCM 256 ciphertext + 128-bit tag of the padded payload
//
// - Key: Argon2id at the SAME floor as the app lock, from a backup passphrase
//   that passes backupPassphrase.ts. The data key never leaves the phone (it is
//   non-extractable): the records are decrypted with it and sealed again here,
//   under the backup key. Photo blobs travel still sealed — each one's key is
//   inside its moment's record, which is inside this payload.
// - Integrity: SHA-256 of magic + length + header is the AES-GCM additional
//   data, so any changed byte — header or content — makes opening fail.
// - openBackup checks everything before it returns: shape, format, versions,
//   cost (refused BEFORE any Argon2id work), then the authenticated decrypt and
//   the payload shape. It writes nothing; the importer writes only after it has
//   succeeded, so a damaged or altered file never touches the diary.
// - Payload: JSON { records, blobs }, padded to a power-of-two size step so the
//   file size says little about how much is written.

import type { computeHash } from 'argon2id'

import {
  aesGcmDecrypt,
  aesGcmEncrypt,
  type Argon2idCost,
  ARGON2ID_FLOOR,
  BelowFloorError,
  type Bytes,
  concatBytes,
  deriveWrappingKey,
  fromBase64,
  fromHex,
  fromUtf8,
  isRecordId,
  meetsFloor,
  pad,
  randomBytes,
  SALT_BYTES,
  secretBytes,
  type SizeRule,
  toBase64,
  toHex,
  unpad,
  utf8,
  wipe,
} from '../crypto'
import { checkLinks, DATA_SCHEMA_VERSION, type DiaryRecord } from '../store'

import { checkBackupPassphrase } from './backupPassphrase'

export const BACKUP_FILE_EXTENSION = '.feelbackup'
export const BACKUP_FORMAT_VERSION = 1

const MAGIC = utf8('PEBBLEBK')
const LENGTH_BYTES = 2
const MAX_HEADER_BYTES = 2048
const NONCE_BYTES = 12
const TAG_BYTES = 16
const KiB = 1024
const MiB = 1024 * KiB

/** Why a file was refused. Wrong passphrase and a changed byte cannot be told apart (both fail the tag). */
export type BackupRefusal = 'not-a-backup' | 'damaged' | 'newer-version' | 'weak-settings' | 'cannot-open'

export class BackupRefusedError extends Error {
  constructor(readonly reason: BackupRefusal) {
    super(`the backup file was refused (${reason})`)
    this.name = 'BackupRefusedError'
  }
}

/** Thrown by makeBackup when the passphrase does not pass the backup policy. */
export class BackupPassphraseError extends Error {
  constructor() {
    super('the backup passphrase does not meet the policy')
    this.name = 'BackupPassphraseError'
  }
}

export interface BackupVersions {
  /** The build that made the file (commit). */
  readonly app: string
  /** Data-schema version of the records (store/schema.ts). */
  readonly schema: number
  /** Content package id → version. */
  readonly packages: Readonly<Record<string, number>>
}

/** A photo blob exactly as stored (sealed under its own photo key). */
export interface BackupBlob {
  readonly id: string
  readonly schemaVersion: number
  readonly iv: Bytes
  readonly ct: Bytes
}

export interface BackupContents {
  readonly records: readonly DiaryRecord[]
  readonly blobs: readonly BackupBlob[]
}

export interface OpenedBackup {
  readonly versions: BackupVersions
  readonly contents: BackupContents
}

interface Header extends BackupVersions {
  readonly format: number
  readonly kdf: 'argon2id'
  readonly cost: Argon2idCost
  readonly salt: Bytes
  readonly iv: Bytes
}

/** Payload size steps: powers of two from 64 KiB to 16 MiB, then whole 16 MiB steps. */
export const backupSize: SizeRule = (contentLength) => {
  const needed = contentLength + 4
  if (needed > 16 * MiB) {
    return Math.ceil(needed / (16 * MiB)) * 16 * MiB
  }
  let step = 64 * KiB
  while (step < needed) {
    step *= 2
  }
  return step
}

// --- header ------------------------------------------------------------------

function encodeHeader(header: Header): Bytes {
  return utf8(
    JSON.stringify({
      format: header.format,
      app: header.app,
      schema: header.schema,
      packages: header.packages,
      kdf: header.kdf,
      cost: { memorySize: header.cost.memorySize, passes: header.cost.passes, parallelism: header.cost.parallelism },
      salt: toHex(header.salt),
      iv: toHex(header.iv),
    }),
  )
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function hasExactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  const own = Object.keys(value)
  return own.length === keys.length && keys.every((key) => Object.hasOwn(value, key))
}

function isPositiveInt(value: unknown): value is number {
  return Number.isInteger(value) && (value as number) >= 1
}

function hexBytes(value: unknown, length: number): Bytes | null {
  if (typeof value !== 'string' || value.length !== length * 2) {
    return null
  }
  try {
    return fromHex(value)
  } catch {
    return null
  }
}

const HEADER_KEYS = ['format', 'app', 'schema', 'packages', 'kdf', 'cost', 'salt', 'iv'] as const
const COST_KEYS = ['memorySize', 'passes', 'parallelism'] as const

/** Parse the clear part. Format is read first, so a newer file is "newer", not "damaged". */
function parseHeader(file: Uint8Array): { header: Header; prefix: Uint8Array; sealed: Uint8Array } {
  const start = MAGIC.length + LENGTH_BYTES
  if (file.length < start || !MAGIC.every((byte, i) => file[i] === byte)) {
    throw new BackupRefusedError('not-a-backup')
  }
  const length = new DataView(file.buffer, file.byteOffset, file.byteLength).getUint16(MAGIC.length)
  if (length === 0 || length > MAX_HEADER_BYTES || start + length > file.length) {
    throw new BackupRefusedError('damaged')
  }
  let raw: unknown
  try {
    raw = JSON.parse(fromUtf8(file.subarray(start, start + length)))
  } catch {
    throw new BackupRefusedError('damaged')
  }
  if (!isPlainObject(raw)) {
    throw new BackupRefusedError('damaged')
  }
  if (isPositiveInt(raw.format) && raw.format > BACKUP_FORMAT_VERSION) {
    throw new BackupRefusedError('newer-version')
  }
  if (raw.format !== BACKUP_FORMAT_VERSION || !hasExactKeys(raw, HEADER_KEYS)) {
    throw new BackupRefusedError('damaged')
  }
  const { app, schema, packages, kdf, cost } = raw
  const salt = hexBytes(raw.salt, SALT_BYTES)
  const iv = hexBytes(raw.iv, NONCE_BYTES)
  if (
    typeof app !== 'string' ||
    app.length === 0 ||
    !isPositiveInt(schema) ||
    !isPlainObject(packages) ||
    !Object.values(packages).every(isPositiveInt) ||
    kdf !== 'argon2id' ||
    !isPlainObject(cost) ||
    !hasExactKeys(cost, COST_KEYS) ||
    !COST_KEYS.every((key) => isPositiveInt(cost[key])) ||
    salt === null ||
    iv === null
  ) {
    throw new BackupRefusedError('damaged')
  }
  const header: Header = {
    format: BACKUP_FORMAT_VERSION,
    app,
    schema,
    packages: packages as Record<string, number>,
    kdf,
    cost: { memorySize: cost.memorySize as number, passes: cost.passes as number, parallelism: cost.parallelism as number },
    salt,
    iv,
  }
  return { header, prefix: file.subarray(0, start + length), sealed: file.subarray(start + length) }
}

async function headerHash(prefix: Uint8Array): Promise<Bytes> {
  return new Uint8Array(await crypto.subtle.digest('SHA-256', new Uint8Array(prefix)))
}

// --- payload -----------------------------------------------------------------

interface PayloadRecord {
  id: string
  kind: string
  data: unknown
  links?: DiaryRecord['links']
}

interface PayloadBlob {
  id: string
  schemaVersion: number
  iv: string
  ct: string
}

function encodePayload(contents: BackupContents): Bytes {
  const records = contents.records.map((r): PayloadRecord => {
    const out: PayloadRecord = { id: r.id, kind: r.kind, data: r.data }
    if (r.links !== undefined) {
      out.links = r.links
    }
    return out
  })
  const blobs = contents.blobs.map(
    (b): PayloadBlob => ({ id: b.id, schemaVersion: b.schemaVersion, iv: toBase64(b.iv), ct: toBase64(b.ct) }),
  )
  return utf8(JSON.stringify({ records, blobs }))
}

function parseRecord(value: unknown): DiaryRecord | null {
  if (!isPlainObject(value)) {
    return null
  }
  const { id, kind, data, links } = value
  const keys = links === undefined ? ['id', 'kind', 'data'] : ['id', 'kind', 'data', 'links']
  if (!hasExactKeys(value, keys) || !isRecordId(id) || typeof kind !== 'string' || kind.length === 0) {
    return null
  }
  if (links === undefined) {
    return { id, kind, data }
  }
  return checkLinks(links) ? { id, kind, data, links } : null
}

function parseBlob(value: unknown): BackupBlob | null {
  if (!isPlainObject(value) || !hasExactKeys(value, ['id', 'schemaVersion', 'iv', 'ct'])) {
    return null
  }
  const { id, schemaVersion, iv, ct } = value
  if (!isRecordId(id) || !isPositiveInt(schemaVersion) || typeof iv !== 'string' || typeof ct !== 'string') {
    return null
  }
  let ivBytes: Bytes
  let ctBytes: Bytes
  try {
    ivBytes = fromBase64(iv)
    ctBytes = fromBase64(ct)
  } catch {
    return null
  }
  if (ivBytes.length !== NONCE_BYTES || ctBytes.length < TAG_BYTES) {
    return null
  }
  return { id, schemaVersion, iv: ivBytes, ct: ctBytes }
}

function parsePayload(plaintext: Uint8Array): BackupContents {
  let raw: unknown
  try {
    raw = JSON.parse(fromUtf8(plaintext))
  } catch {
    throw new BackupRefusedError('damaged')
  }
  if (!isPlainObject(raw) || !hasExactKeys(raw, ['records', 'blobs']) || !Array.isArray(raw.records) || !Array.isArray(raw.blobs)) {
    throw new BackupRefusedError('damaged')
  }
  const records = raw.records.map(parseRecord)
  const blobs = raw.blobs.map(parseBlob)
  const ids = new Set<string>()
  for (const item of [...records, ...blobs]) {
    if (item === null || ids.has(item.id)) {
      throw new BackupRefusedError('damaged')
    }
    ids.add(item.id)
  }
  return { records: records as DiaryRecord[], blobs: blobs as BackupBlob[] }
}

// --- make / open -------------------------------------------------------------

/**
 * Seal the contents into one `.feelbackup` file under a key derived from the
 * backup passphrase (Argon2id at the floor, new salt and nonce every time).
 * Refuses a passphrase that does not pass the backup policy, before any work.
 */
export async function makeBackup(
  hash: computeHash,
  passphrase: string,
  versions: BackupVersions,
  contents: BackupContents,
): Promise<Bytes> {
  if (!checkBackupPassphrase(passphrase).ok) {
    throw new BackupPassphraseError()
  }
  const header: Header = {
    format: BACKUP_FORMAT_VERSION,
    app: versions.app,
    schema: versions.schema,
    packages: { ...versions.packages },
    kdf: 'argon2id',
    cost: { ...ARGON2ID_FLOOR },
    salt: randomBytes(SALT_BYTES),
    iv: randomBytes(NONCE_BYTES),
  }
  const headerBytes = encodeHeader(header)
  if (headerBytes.length > MAX_HEADER_BYTES) {
    throw new Error('backup header too large')
  }
  const length = new Uint8Array(LENGTH_BYTES)
  new DataView(length.buffer).setUint16(0, headerBytes.length)
  const prefix = concatBytes(MAGIC, length, headerBytes)

  const secret = secretBytes(passphrase)
  const plaintext = encodePayload(contents)
  const padded = pad(plaintext, backupSize)
  try {
    const key = await deriveWrappingKey(hash, secret, header.salt, header.cost)
    const sealed = await aesGcmEncrypt(key, header.iv, await headerHash(prefix), padded)
    return concatBytes(prefix, sealed)
  } finally {
    wipe(secret)
    wipe(plaintext)
    wipe(padded)
  }
}

/** Read the clear header only (no key work): the versions, or a refusal. */
export function readBackupVersions(file: Uint8Array): BackupVersions {
  const { header } = parseHeader(file)
  return { app: header.app, schema: header.schema, packages: header.packages }
}

/**
 * Check and open a backup file. Every refusal happens here, before the caller
 * writes anything. A newer format or data schema, and a cost below the floor,
 * are refused before Argon2id runs.
 */
export async function openBackup(
  hash: computeHash,
  file: Uint8Array,
  passphrase: string,
  appSchemaVersion: number = DATA_SCHEMA_VERSION,
): Promise<OpenedBackup> {
  const { header, prefix, sealed } = parseHeader(file)
  if (header.schema > appSchemaVersion) {
    throw new BackupRefusedError('newer-version')
  }
  if (!meetsFloor(header.cost)) {
    throw new BackupRefusedError('weak-settings')
  }
  if (sealed.length < TAG_BYTES) {
    throw new BackupRefusedError('damaged')
  }

  const secret = secretBytes(passphrase)
  let padded: Bytes
  try {
    const key = await deriveWrappingKey(hash, secret, header.salt, header.cost)
    padded = await aesGcmDecrypt(key, header.iv, await headerHash(prefix), new Uint8Array(sealed))
  } catch (error) {
    throw new BackupRefusedError(error instanceof BelowFloorError ? 'weak-settings' : 'cannot-open')
  } finally {
    wipe(secret)
  }

  let plaintext: Bytes
  try {
    plaintext = unpad(padded)
  } catch {
    throw new BackupRefusedError('damaged')
  } finally {
    wipe(padded)
  }
  try {
    return {
      versions: { app: header.app, schema: header.schema, packages: header.packages },
      contents: parsePayload(plaintext),
    }
  } finally {
    wipe(plaintext)
  }
}
